/* Live Screen ✦ — new scene components (iter 170).
 *
 * Three projector-grade scenes that extend the cinematic VenueLivePage
 * rotation. Each follows the established contract:
 *   • full-bleed 16:9 stage
 *   • soft-fade transitions only (no hard cuts, no particle chaos)
 *   • renders nothing when there's no data (no dead frames)
 *   • cinematic spacing — let the scene BREATHE
 *
 * Design language: walnut/ivory/gold. Same typography family
 * (Cormorant Garamond display, Inter body) as the Drop One Word ✦
 * mobile wall — the same atmosphere, projected.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INK = '#0c0805';
const INK_2 = '#1a120a';
const IVORY = '#f5efdf';
const IVORY_SOFT = 'rgba(245,239,223,0.74)';
const GOLD = '#c19a4b';
const HAIRLINE = 'rgba(245,239,223,0.16)';
const FONT_DISPLAY = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";
const FONT_BODY = "'Inter', system-ui, sans-serif";

const sceneStage = {
  position: 'absolute', inset: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: `radial-gradient(ellipse at 50% 40%, ${INK_2} 0%, ${INK} 75%)`,
  overflow: 'hidden',
};

// ────────────────────────────────────────────────────────────────────
//  Scene — Drop One Word ✦ wall (P0 strategic anchor)
// ────────────────────────────────────────────────────────────────────

const MAX_VISIBLE_WORDS_PROJECTOR = 32;

function strHash(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return Math.abs(h);
}

/**
 * Projector-scale ring layout — 32 words, four concentric rings on a
 * 16:9 stage. The centerpiece is intentionally large (~120px) so the
 * dominant feeling reads from the back of a long room. Outer rings
 * sit at smaller sizes for atmospheric depth.
 */
function layoutWordsProjector(words) {
  const visible = words.slice(0, MAX_VISIBLE_WORDS_PROJECTOR);
  if (visible.length === 0) return [];

  const centerWeight = visible[0]?.weight || 1;
  const innerR = Math.max(14, 12 + centerWeight * 1.4);

  const rings = [
    { count: 1,  radius: 0,            fontMul: 1.0 },
    { count: 4,  radius: innerR,       fontMul: 0.55 },
    { count: 8,  radius: innerR + 11,  fontMul: 0.42 },
    { count: 12, radius: innerR + 22,  fontMul: 0.32 },
    { count: 7,  radius: innerR + 32,  fontMul: 0.26 },
  ];

  // Base font ramp at projector scale: centerpiece ~120px, outer ~30px.
  // weight is in [1.0, 5.0]; px = 40 + weight * 16 ≈ [56, 120].
  const baseFontFor = (weight, mul) => {
    const px = 40 + weight * 16;
    return Math.max(28, Math.round(px * mul));
  };

  const placed = [];
  let idx = 0;
  rings.forEach((ring, ringIdx) => {
    const ringWords = visible.slice(idx, idx + ring.count);
    const n = ringWords.length;
    if (!n) return;
    ringWords.forEach((w, i) => {
      const h = strHash(w.normalized_word);
      const ringPhase = ringIdx * (Math.PI / 7);
      const baseAngle = n === 1 ? 0 : (i / n) * Math.PI * 2 + ringPhase;
      const jitter = ((h % 100) / 100 - 0.5) * (Math.PI / n) * 0.45;
      const angle = baseAngle + jitter;
      const radiusJitter = ((h >> 4) % 100) / 100 * 3 - 1.5;
      const radius = ring.radius + radiusJitter;
      const x = Math.max(6, Math.min(94, 50 + radius * Math.cos(angle) * 1.4));
      const y = Math.max(12, Math.min(88, 50 + radius * Math.sin(angle) * 0.85));
      const opacity = Math.max(0.55, Math.min(0.92, 0.6 + w.weight * 0.06));
      const dx = ((h >> 8) % 6) - 3;
      const dy = ((h >> 12) % 6) - 3;
      const delay = (ringIdx * 180) + i * 60;
      placed.push({
        word: w,
        weight: w.weight,
        isFresh: !!w.is_fresh,
        x, y, opacity, dx, dy, delay,
        fontSize: baseFontFor(w.weight, ring.fontMul),
      });
    });
    idx += ring.count;
  });
  return placed;
}

export const SceneDropOneWord = ({ slug }) => {
  const [data, setData] = useState({ total: 0, unique: 0, words: [] });

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/one-word/event/${slug}/wall`);
        if (!cancelled) setData(r.data);
      } catch { /* ignore — empty wall renders gracefully */ }
    };
    load();
    const t = setInterval(load, 15000); // 15s — slow refresh, ambient
    return () => { cancelled = true; clearInterval(t); };
  }, [slug]);

  const positions = useMemo(() => layoutWordsProjector(data.words || []), [data.words]);

  return (
    <div style={sceneStage} data-testid="venue-scene-drop-one-word">
      <style>{`
        @keyframes vlDriftSlow {
          0%, 100% { transform: translate(-50%, -50%); }
          50%      { transform: translate(calc(-50% + var(--dx,0px)), calc(-50% + var(--dy,0px))); }
        }
        @keyframes vlFreshPulse {
          0%, 100% { text-shadow: 0 0 14px rgba(193,154,75,0.18); }
          50%      { text-shadow: 0 0 36px rgba(193,154,75,0.55); }
        }
      `}</style>

      {/* Eyebrow line */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'absolute', top: '5vh', left: 0, right: 0,
          textAlign: 'center', zIndex: 2,
        }}
      >
        <div style={{
          fontSize: 14, fontWeight: 700, letterSpacing: '0.36em',
          textTransform: 'uppercase', color: GOLD,
        }}>✦ The room, in its own words</div>
      </motion.div>

      {/* Word field */}
      <div style={{ position: 'absolute', inset: 0 }}>
        {positions.map((p, i) => (
          <span
            key={p.word.normalized_word}
            data-testid={`venue-word-${p.word.normalized_word}`}
            data-fresh={p.isFresh ? '1' : '0'}
            style={{
              position: 'absolute',
              left: `${p.x}%`, top: `${p.y}%`,
              transform: 'translate(-50%, -50%)',
              fontFamily: FONT_DISPLAY, fontStyle: 'italic',
              fontSize: `${p.fontSize}px`,
              color: p.isFresh ? GOLD : IVORY,
              opacity: p.opacity,
              '--dx': `${p.dx}px`, '--dy': `${p.dy}px`,
              letterSpacing: '-0.01em', whiteSpace: 'nowrap',
              animation: `vlDriftSlow ${14 + (p.weight * 1.2)}s ease-in-out infinite ${p.delay}ms${p.isFresh ? `, vlFreshPulse 6s ease-in-out infinite ${p.delay}ms` : ''}`,
              textShadow: p.weight > 3.2 ? `0 0 40px rgba(193,154,75,0.25)` : 'none',
              pointerEvents: 'none',
              willChange: 'transform, opacity',
            }}
          >
            {p.word.word}
          </span>
        ))}
        {positions.length === 0 && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <p style={{
              fontFamily: FONT_DISPLAY, fontStyle: 'italic',
              fontSize: 44, color: IVORY_SOFT, textAlign: 'center',
            }}>The room is quiet yet ✦</p>
          </div>
        )}
      </div>

      {/* Footer count */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.6, delay: 0.4 }}
        style={{
          position: 'absolute', bottom: '5vh', left: 0, right: 0,
          textAlign: 'center', zIndex: 2,
          fontFamily: FONT_DISPLAY, fontStyle: 'italic',
          fontSize: 18, color: IVORY_SOFT, letterSpacing: '0.04em',
        }}
      >{data.total > 0 ? `${data.total} ${data.total === 1 ? 'feeling' : 'feelings'} held tonight` : ''}</motion.div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Scene — Memory Wall ticker (P0 recognition energy)
// ────────────────────────────────────────────────────────────────────

/**
 * Slow-moving editorial strip of the 6 latest captures.
 *
 * Auto-pans left at ~12px/s — slow enough to read each photo, fast
 * enough that newcomers see motion. The just-arrived tile (most
 * recent) carries a brief gold-edge glow.
 *
 * Lens Notes (MP4) auto-play muted inline; static photos render as
 * still images. The masonry is deliberately uniform — no rapid
 * reflow on new arrival (one-in, one-out, never jolting).
 */
const TICKER_VISIBLE_TILES = 6;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve a backend-relative media URL ("/api/photos/...") to an
// absolute URL the projector can <img src>. The wall and dashboard
// follow the same idiom — see MemoryWallPage / MemorySnapPage.
const resolveMedia = (u) => {
  if (!u) return null;
  if (typeof u !== 'string') return null;
  if (u.startsWith('http')) return u;
  return `${BACKEND_URL}${u}`;
};

export const SceneMemoryWallTicker = ({ eventId, eventName }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        // Pull from two sources concurrently and merge by created_at.
        // iter182 — field-name alignment with the actual backend
        // shapes. Earlier iter170 code assumed `p.url`, `s.image_url`,
        // and `lensR.data.notes` — none of which the API actually
        // returns. The triple-mismatch silently filtered EVERY
        // photo, snap, and lens note out of the projector ticker,
        // making the Live Screen feel empty even at packed events.
        const [photosR, snapsR, lensR] = await Promise.all([
          axios.get(`${API}/events/${eventId}/photos`).catch(() => ({ data: [] })),
          axios.get(`${API}/events/${eventId}/memory-snaps`).catch(() => ({ data: { snaps: [] } })),
          axios.get(`${API}/lens-notes/event/${eventId}`).catch(() => ({ data: { lens_notes: [] } })),
        ]);
        // Photos endpoint returns Photo models with `storage_path`
        // only (no resolved URL). Resolve via the same /api/photos
        // serving route every other surface uses.
        const photos = (photosR.data || []).map((p) => ({
          id: p.photo_id,
          kind: 'photo',
          url: p.storage_path ? resolveMedia(`/api/photos/${p.storage_path}`) : null,
          ts: p.uploaded_at,
        }));
        // Memory snaps endpoint resolves `photo_url` server-side
        // (already prefixed with `/api/photos/...`). Each snap may
        // also be a lens-note row (kind === 'lens_note') in which
        // case we want the video, not the still.
        const snaps = ((snapsR.data && snapsR.data.snaps) || []).map((s) => {
          if (s.kind === 'lens_note') {
            return {
              id: s.lens_note_id || s.snap_id,
              kind: 'lens',
              url: resolveMedia(s.lens_note_video_url),
              poster: resolveMedia(s.photo_url),
              ts: s.created_at,
            };
          }
          return {
            id: s.snap_id,
            kind: 'snap',
            url: resolveMedia(s.photo_url),
            ts: s.created_at,
          };
        });
        // Lens Notes — direct list endpoint returns `lens_notes`
        // (NOT `notes` as the iter170 code expected). Items carry
        // `lens_note_id`, `mp4_url`, `poster_url`.
        const lens = ((lensR.data && lensR.data.lens_notes) || []).map((n) => ({
          id: n.lens_note_id,
          kind: 'lens',
          url: resolveMedia(n.mp4_url),
          poster: resolveMedia(n.poster_url),
          ts: n.created_at,
        }));
        // Dedupe — lens notes appear in both the memory-snaps merge
        // AND the lens-notes endpoint. Last one wins via Map keying.
        const byId = new Map();
        for (const item of [...photos, ...snaps, ...lens]) {
          if (item.url) byId.set(item.id, item);
        }
        const merged = Array.from(byId.values())
          .sort((a, b) => String(b.ts || '').localeCompare(String(a.ts || '')))
          .slice(0, TICKER_VISIBLE_TILES);
        if (!cancelled) setItems(merged);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 12000);
    return () => { cancelled = true; clearInterval(t); };
  }, [eventId]);

  if (items.length === 0) {
    // Nothing yet — render the calm empty state (not a dead frame)
    return (
      <div style={sceneStage} data-testid="venue-scene-memory-wall-empty">
        <p style={{
          fontFamily: FONT_DISPLAY, fontStyle: 'italic',
          fontSize: 42, color: IVORY_SOFT, textAlign: 'center', padding: 60,
        }}>
          The wall is empty yet ✦<br />
          <span style={{ fontSize: 20, color: 'rgba(245,239,223,0.5)' }}>
            The first upload becomes the first memory.
          </span>
        </p>
      </div>
    );
  }

  const newestId = items[0]?.id;

  return (
    <div style={sceneStage} data-testid="venue-scene-memory-wall">
      <style>{`
        @keyframes vlTickerPan {
          0% { transform: translateX(2%); }
          100% { transform: translateX(-2%); }
        }
        @keyframes vlNewGlow {
          0%, 100% { box-shadow: 0 0 0 1px rgba(193,154,75,0.25), 0 0 40px rgba(193,154,75,0.15); }
          50%      { box-shadow: 0 0 0 2px rgba(193,154,75,0.65), 0 0 80px rgba(193,154,75,0.45); }
        }
      `}</style>

      {/* Eyebrow */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2 }}
        style={{
          position: 'absolute', top: '6vh', left: 0, right: 0,
          textAlign: 'center', zIndex: 2,
        }}
      >
        <div style={{
          fontSize: 14, fontWeight: 700, letterSpacing: '0.36em',
          textTransform: 'uppercase', color: GOLD, marginBottom: 14,
        }}>✦ The wall, tonight</div>
        <div style={{
          fontFamily: FONT_DISPLAY, fontSize: 36, color: IVORY,
          fontStyle: 'italic', letterSpacing: '-0.005em',
        }}>{eventName || 'The room'}</div>
      </motion.div>

      {/* Strip */}
      <div
        style={{
          position: 'absolute', left: 0, right: 0, top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 28, padding: '0 60px',
          animation: 'vlTickerPan 22s ease-in-out infinite alternate',
        }}
      >
        {items.map((it, i) => {
          const isNewest = it.id === newestId;
          const baseStyle = {
            width: 'clamp(180px, 14vw, 220px)',
            aspectRatio: '3 / 4',
            borderRadius: 10,
            overflow: 'hidden',
            background: '#1a120a',
            border: `1px solid ${HAIRLINE}`,
            flexShrink: 0,
            animation: isNewest ? 'vlNewGlow 4s ease-in-out infinite' : undefined,
            transform: `rotate(${(i % 2 === 0 ? -1 : 1) * 1.2}deg)`,
            transition: 'transform 600ms cubic-bezier(0.16,1,0.3,1)',
          };
          return (
            <div
              key={it.id}
              data-testid={`venue-tile-${it.kind}-${it.id}`}
              data-newest={isNewest ? '1' : '0'}
              style={baseStyle}
            >
              {it.kind === 'lens' ? (
                <video
                  src={it.url}
                  poster={it.poster}
                  autoPlay loop muted playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  backgroundImage: `url(${it.url})`,
                  backgroundSize: 'cover', backgroundPosition: 'center',
                }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Scene — QR Plate (P1 latecomer doorway)
// ────────────────────────────────────────────────────────────────────

import { QRCodeSVG } from 'qrcode.react';

export const SceneQRPlate = ({ slug, eventName }) => {
  const url = `${window.location.origin}/access/${slug}`;
  return (
    <div style={sceneStage} data-testid="venue-scene-qr-plate">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        style={{ textAlign: 'center', position: 'relative', zIndex: 2 }}
      >
        <div style={{
          fontSize: 14, fontWeight: 700, letterSpacing: '0.36em',
          textTransform: 'uppercase', color: GOLD, marginBottom: 20,
        }}>✦ Tonight</div>
        <h1 style={{
          margin: '0 0 14px', fontFamily: FONT_DISPLAY,
          fontSize: 'clamp(56px, 7vw, 92px)', lineHeight: 1.05,
          color: IVORY, letterSpacing: '-0.01em',
        }}>Step into tonight ✦</h1>
        <p style={{
          margin: '0 auto 44px', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
          fontSize: 'clamp(22px, 2vw, 28px)', color: IVORY_SOFT, letterSpacing: '0.04em',
        }}>Photos · seating · memories · Lens Notes</p>

        {/* The plate itself — ivory card, walnut QR */}
        <div style={{
          display: 'inline-block', padding: 'clamp(22px, 2.4vw, 32px)',
          background: '#F5F0E4', borderRadius: 8,
          boxShadow: '0 30px 100px rgba(0,0,0,0.55), 0 0 0 1px rgba(193,154,75,0.25)',
        }}>
          <QRCodeSVG
            value={url}
            size={Math.min(360, Math.max(220, Math.round(window.innerWidth * 0.18)))}
            level="H"
            fgColor="#1B140A"
            bgColor="#F5F0E4"
            includeMargin={false}
          />
          {eventName && (
            <div style={{
              marginTop: 18, fontFamily: FONT_DISPLAY,
              fontSize: 16, color: 'rgba(40,32,18,0.7)',
              fontStyle: 'italic', letterSpacing: '0.02em',
            }}>{eventName}</div>
          )}
        </div>

        <p style={{
          margin: '36px auto 0',
          fontFamily: FONT_BODY, fontSize: 12,
          fontWeight: 700, letterSpacing: '0.32em',
          color: 'rgba(245,239,223,0.4)', textTransform: 'uppercase',
        }}>Scan with your camera</p>
      </motion.div>
    </div>
  );
};
